import type { Attribute, Common } from "@strapi/types";
import { notFound } from "next/navigation";
import qs from "qs";

import { Locale } from "@/i18n";
import {
  GetType,
  GetValues,
  Model,
  PaginationByOffset,
  PaginationByPage,
  Response,
  ResponseCollection,
  ReverseModel,
  ReversePluralModel,
  ReverseSeoModel,
  ReverseSingularModel,
  WhereParams,
} from "@/types/types";

// type x = Response<ReverseSingularModel['spinoff']>;

/**
 * Get full Strapi URL from path
 * @param path Path of the URL
 * @returns Full Strapi URL
 */
export function getStrapiURL(path = "") {
  return `${
    process.env.NEXT_PUBLIC_STRAPI_API_URL ?? "https://cms.silberheim.com"
  }${path}`;
}

function getBaseUrl() {
  return process.env.NEXT_PUBLIC_STRAPI_API_URL ?? "https://cms.silberheim.com";
}

interface FetchParam<
  T extends keyof Model,
  Dto extends GetValues<T> = GetValues<T>,
> {
  fields?: Attribute.GetKeys<T>[];
  filters?: WhereParams<Dto>;
  pagination?: PaginationByOffset | PaginationByPage;
  populate?: "*" | "deep";
  sort?: Attribute.GetKeys<T>[] | "id" | Attribute.GetKeys<T>;
  locale: Locale;
}

export async function fetchAPI<
  T extends keyof ReverseSingularModel,
  TParams extends FetchParam<ReverseSingularModel[T]>,
>(resource: T, params?: TParams): Promise<Response<ReverseSingularModel[T]>>;
export async function fetchAPI<
  T extends keyof ReversePluralModel,
  TParams extends FetchParam<ReversePluralModel[T]>,
>(
  resource: T,
  params?: TParams,
): Promise<ResponseCollection<ReversePluralModel[T]>>;
export async function fetchAPI<
  T extends `${keyof ReversePluralModel}/${number}`,
  TModel extends
    (T extends `${infer InferT extends keyof ReversePluralModel}/${number}`
      ? InferT
      : never),
  TParams extends FetchParam<ReverseModel[TModel]>,
>(resource: T, params?: TParams): Promise<Response<ReverseModel[TModel]>>;
export async function fetchAPI<
  T extends keyof ReverseSeoModel,
  TParams extends FetchParam<ReverseSeoModel[T]>,
>(resource: T, params?: TParams): Promise<Response<ReverseSeoModel[T]>>;
export async function fetchAPI<
  T extends keyof ReverseModel,
  TResPath extends T | `${T}/${number}` | `${T}/${string}`,
  TParams extends FetchParam<ReverseModel[T]>,
  Ret extends Response<ReverseModel[T]> | ResponseCollection<ReverseModel[T]>,
>(resource: TResPath, params: TParams = {} as TParams): Promise<Ret> {
  const options = {
    headers: {
      "Content-Type": "application/json",
    },
    next: {
      revalidate: 10,
    },
  };

  const queryString = params ? qs.stringify(params) : "";

  const url = new URL(`/api/${resource}?${queryString}`, getBaseUrl());
  const response = await fetch(url, options);
  const payload = (await response.json()) as Ret;

  if (response.ok) {
    return payload;
  }

  console.error(
    `Fetch failed for ${resource}`,
    response.status,
    response.statusText,
  );
  return { data: null } as Ret;
}

export function transformOrNotFound<T extends Common.UID.Schema>(
  response: Response<T>,
): GetType<T>;
export function transformOrNotFound<T extends Common.UID.Schema>(
  response: ResponseCollection<T>,
): GetType<T>[];
export function transformOrNotFound<T extends Common.UID.Schema>(
  response: Response<T> | ResponseCollection<T>,
): GetType<T> | GetType<T>[] {
  if (response.data === null) {
    // TODO: is this correct?
    notFound();
  }

  if (Array.isArray(response.data)) {
    return response.data.map((x) => ({
      id: x.id,
      ...x.attributes,
    })) as GetType<T>[];
  }

  return {
    id: response.data.id,
    ...response.data.attributes,
  } as GetType<T>;
}
